/**
 * @file InstallationTab.jsx
 * @description Hardware Installation — laid out like Registration: the forms
 *              on the left, the register of what is fitted on the right.
 *
 *              START AN INSTALLATION — mapping a TCU onto a machine the way it
 *              happens in the field: pick device + machine, wire up while the
 *              live ladder turns green, then the meter cross-check — type what
 *              the MACHINE DISPLAY shows and the server compares it with what
 *              the TCU reports (±2 h, ±10 %). Commissioning is the step that
 *              writes the device→machine mapping and files the evidence
 *              (checks snapshot, meters, who, when).
 *
 *              EDIT AN INSTALLATION — the correction path for a mapping that
 *              is already written: move a fitted TCU onto a different machine,
 *              or take it off one. Deliberately not a quiet UPDATE — it is
 *              confirmed first and filed as a REMAPPED / UNMAPPED row in the
 *              history below, because the mapping decides which machine every
 *              fleet screen attributes this unit's telemetry to.
 *
 *              INSTALLED DEVICES — the right-hand panel: every TCU currently on
 *              a machine, searchable by IMEI / serial / machine. Clicking a row
 *              loads it into the edit card.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, Typography, Button, Stack, Alert, Chip, TextField, Autocomplete, Grid,
    Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
    Build as InstallIcon, CheckCircle as CommissionIcon, Cancel as FailIcon,
    SwapHoriz as RemapIcon, LinkOff as UnmapIcon, Refresh as RefreshIcon,
    Search as SearchIcon, EditOutlined as EditIcon, MemoryOutlined as TcuIcon,
} from '@mui/icons-material';
import hardwareLifecycleService from '../../../services/hardwareLifecycleService';
import LiveCheckPanel from './LiveCheckPanel';
import CanCheckPanel from './CanCheckPanel';
import { formatToIST } from '../../../utils/dataUtils';
import { machineLabel } from '../../../utils/machineLabel';

const fmt = (ts) => {
    if (!ts) return '—';
    const out = formatToIST(ts, 'dd MMM · HH:mm');
    return out === 'N/A' || out === 'Invalid Date' ? '—' : out;
};

/** apiClient throws `API Error: 409 Conflict - {"error":"…"}` — show the sentence. */
const friendly = (e) => {
    const m = String(e?.message || '');
    try {
        const parsed = JSON.parse(m.slice(m.indexOf('{')));
        if (parsed?.error) return parsed.error;
    } catch { /* not an envelope */ }
    return m.replace(/^API Error: \d+ [^-]*- /, '') || 'Something went wrong';
};

const machineOf = (machines, equipmentId) =>
    machines.find(m => m.equipment_id === equipmentId) || null;

/** One fitted TCU in the right-hand register. Click → it opens in the edit card. */
const InstalledRow = ({ d, selected, onPick }) => (
    <Box
        onClick={() => onPick(d)}
        sx={{
            px: 1.25, py: 1, borderRadius: '10px', cursor: 'pointer',
            border: '1px solid', borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'primary.50' : 'background.paper',
            display: 'flex', alignItems: 'center', gap: 1,
            transition: 'background-color .12s, border-color .12s',
            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
        }}
    >
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }} noWrap>
                {d.device_id}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {d.equipment_id || 'not installed'}
            </Typography>
        </Box>
        <Chip size="small" variant={d.equipment_id ? 'filled' : 'outlined'}
            color={d.equipment_id ? 'primary' : 'default'}
            label={d.equipment_id ? machineLabel(d) : 'Spare'}
            sx={{ height: 22, fontWeight: 700, maxWidth: 160 }} />
    </Box>
);

const InstallationTab = () => {
    const [pickers, setPickers] = useState(null);
    const [device, setDevice] = useState(null);            // devices row
    const [machine, setMachine] = useState(null);          // machines row
    const [session, setSession] = useState(null);          // {id, device_id, equipment_id}
    const [live, setLive] = useState(null);                // latest live-check (from the panel)
    const [displayHours, setDisplayHours] = useState('');
    const [displayFuel, setDisplayFuel] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState(null);          // pending forced commission
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    // ── Edit a finished installation ─────────────────────────────────────────
    const [picked, setPicked] = useState(null);            // the fitted device as stored
    const [moveTo, setMoveTo] = useState(null);            // the machine to move it to
    const [editNotes, setEditNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [editDone, setEditDone] = useState('');
    const [ask, setAsk] = useState(null);                  // {kind:'remap'|'unmap', message?, force}

    // ── The register of fitted TCUs (right-hand panel) ───────────────────────
    const [query, setQuery] = useState('');
    const [fitment, setFitment] = useState('installed');   // installed | spare | all

    const load = useCallback(async () => {
        try {
            const [p, h] = await Promise.all([
                hardwareLifecycleService.pickers(),
                hardwareLifecycleService.listInstallations(20),
            ]);
            setPickers(p);
            setHistory(h.data || []);
            setError('');
            return p;
        } catch (e) {
            setError(friendly(e));
            return null;
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const start = async () => {
        if (!device || !machine) { setError('Pick both the device and the machine'); return; }
        setBusy(true); setError('');
        try {
            const r = await hardwareLifecycleService.startInstallation(device.device_id, machine.equipment_id);
            setSession({ id: r.id, device_id: device.device_id, equipment_id: machine.equipment_id,
                         serial_number: machine.serial_number });
            setDisplayHours(''); setDisplayFuel(''); setNotes(''); setLive(null);
        } catch (e) {
            setError(friendly(e));
        } finally {
            setBusy(false);
        }
    };

    const finish = async (action, force = false) => {
        setBusy(true); setError(''); setConfirm(null);
        try {
            await hardwareLifecycleService.finishInstallation(session.id, {
                action,
                force,
                display_engine_hours: displayHours === '' ? null : Number(displayHours),
                display_fuel_pct: displayFuel === '' ? null : Number(displayFuel),
                notes,
            });
            setSession(null); setDevice(null); setMachine(null);
            await load();
        } catch (e) {
            // A 409 means a guard fired (checks not green / machine occupied) —
            // surface it and offer the deliberate override.
            if (/409/.test(e.message)) setConfirm({ message: friendly(e) });
            else setError(friendly(e));
        } finally {
            setBusy(false);
        }
    };

    const pick = (d) => {
        setPicked(d);
        setMoveTo(null);
        setEditNotes('');
        setEditError('');
        setEditDone('');
    };

    /**
     * Apply the mapping change. `force` confirms displacing whatever device is
     * already on the target machine — the server refuses without it, and that
     * refusal is what raises the confirm dialog.
     */
    const applyMapping = async (kind, force = false) => {
        if (!picked) return;
        setSaving(true); setEditError(''); setEditDone(''); setAsk(null);
        try {
            const r = await hardwareLifecycleService.updateDeviceMapping(
                picked.device_id,
                kind === 'unmap' ? null : moveTo?.equipment_id,
                { notes: editNotes, force });
            setEditDone(r.action === 'unmapped'
                ? `${picked.device_id} taken off ${r.from} — it is a spare again.`
                : `${picked.device_id} moved${r.from ? ` from ${r.from}` : ''} onto ${machineLabel(moveTo) || r.to}.${r.displaced ? ` ${r.displaced} is now a spare.` : ''}`);
            const p = await load();
            // Re-read the unit so the card shows what is actually STORED.
            const fresh = (p?.devices || []).find(d => d.device_id === picked.device_id) || null;
            setPicked(fresh);
            setMoveTo(null);
            setEditNotes('');
        } catch (e) {
            if (/409/.test(e.message)) setAsk({ kind, message: friendly(e) });
            else setEditError(friendly(e));
        } finally {
            setSaving(false);
        }
    };

    // Live comparison preview, same tolerances the server enforces.
    const telemetryHours = live?.meters?.engine_hours?.value ?? null;
    const telemetryFuel = live?.meters?.fuel_pct?.value ?? null;
    const hoursDelta = displayHours !== '' && telemetryHours != null ? +(Number(displayHours) - telemetryHours).toFixed(1) : null;
    const fuelDelta = displayFuel !== '' && telemetryFuel != null ? +(Number(displayFuel) - telemetryFuel).toFixed(1) : null;
    const hoursOk = hoursDelta == null ? null : Math.abs(hoursDelta) <= 2;
    const fuelOk = fuelDelta == null ? null : Math.abs(fuelDelta) <= 10;

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

    const devices = pickers?.devices || [];
    const machines = pickers?.machines || [];
    const installedCount = devices.filter(d => d.equipment_id).length;
    const currentMachine = picked ? machineOf(machines, picked.equipment_id) : null;

    const visible = devices.filter(d => {
        if (fitment === 'installed' && !d.equipment_id) return false;
        if (fitment === 'spare' && d.equipment_id) return false;
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return [d.device_id, d.serial_number, d.equipment_id]
            .some(v => String(v || '').toLowerCase().includes(q));
    });

    // A commissioning run takes the whole width — the installer is watching the
    // ladder and has no use for the register while wiring.
    if (session) {
        return (
            <Box>
                {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, mb: 2 }}>
                    <LiveCheckPanel deviceId={session.device_id} onData={setLive} />
                    <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5, alignSelf: 'flex-start' }}>
                        <Typography variant="subtitle1" fontWeight={800}>
                            Commission — {machineLabel(session)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Type what the machine display shows; it is compared with the TCU's reading.
                        </Typography>
                        <Stack spacing={1.5}>
                            <Box>
                                <TextField label="Display engine hours" value={displayHours} type="number"
                                    onChange={(e) => setDisplayHours(e.target.value)} size="small" fullWidth />
                                {hoursOk != null && (
                                    <Typography variant="caption" sx={{ color: hoursOk ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                                        TCU reports {telemetryHours} h · Δ {hoursDelta} h — {hoursOk ? 'matches (±2 h)' : 'MISMATCH'}
                                    </Typography>
                                )}
                            </Box>
                            <Box>
                                <TextField label="Display fuel %" value={displayFuel} type="number"
                                    onChange={(e) => setDisplayFuel(e.target.value)} size="small" fullWidth />
                                {fuelOk != null && (
                                    <Typography variant="caption" sx={{ color: fuelOk ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                                        TCU reports {telemetryFuel} % · Δ {fuelDelta} — {fuelOk ? 'matches (±10)' : 'MISMATCH'}
                                    </Typography>
                                )}
                            </Box>
                            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                                multiline minRows={2} size="small" fullWidth />
                            <Stack direction="row" spacing={1}>
                                <Button color="success" variant="contained" onClick={() => finish('commission')}
                                    disabled={busy} startIcon={<CommissionIcon />}
                                    sx={{ fontWeight: 700, textTransform: 'none' }}>
                                    Commission
                                </Button>
                                <Button color="error" variant="outlined" onClick={() => finish('fail')}
                                    disabled={busy} startIcon={<FailIcon />}
                                    sx={{ fontWeight: 700, textTransform: 'none' }}>
                                    Mark failed
                                </Button>
                                <Button color="inherit" onClick={() => setSession(null)} disabled={busy}
                                    sx={{ textTransform: 'none' }}>
                                    Abandon
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>
                </Box>

                {/* The CHECKING step: every CAN frame this machine's model must
                    transmit (machine_can_ids), green tick / red cross as they
                    arrive — the installer debugs the bus until all are green. */}
                <CanCheckPanel deviceId={session.device_id} equipmentId={session.equipment_id} />

                <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Commission anyway?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{confirm?.message}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirm(null)}>Back</Button>
                        <Button color="warning" variant="contained" onClick={() => finish('commission', true)}
                            sx={{ fontWeight: 700, textTransform: 'none' }}>
                            Force commission
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

            <Grid container spacing={2.5} alignItems="flex-start">
                {/* ── The forms: start one, then edit one ────────────────────── */}
                <Grid size={{ xs: 12, lg: 7 }}>
                  <Stack spacing={2.5}>
                    <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.25 }}>Start an installation</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            The mapping is written only at commissioning — after the checks are green and the meters agree.
                        </Typography>
                        <Stack spacing={1.5}>
                            <Autocomplete
                                options={[...devices].sort((a, b) => (a.equipment_id ? 1 : 0) - (b.equipment_id ? 1 : 0))}
                                groupBy={(d) => (d.equipment_id ? 'Already mapped (re-install)' : 'Unmapped devices')}
                                getOptionLabel={(d) => `${d.device_id}${d.serial_number ? ` · ${d.serial_number}` : ''}${d.equipment_id ? ` → ${d.equipment_id}` : ''}`}
                                value={device}
                                isOptionEqualToValue={(o, v) => o.device_id === v.device_id}
                                onChange={(_, v) => setDevice(v)}
                                renderInput={(p) => <TextField {...p} label="TCU device" size="small" />}
                            />
                            <Autocomplete
                                options={[...machines].sort((a, b) => (a.mapped_device ? 1 : 0) - (b.mapped_device ? 1 : 0))}
                                groupBy={(m) => (m.mapped_device ? 'Already has a device' : 'Free machines')}
                                getOptionLabel={(m) => `${m.serial_number || m.equipment_id} · ${[m.brand, m.model_number].filter(Boolean).join(' ')}${m.mapped_device ? ` (device: ${m.mapped_device})` : ''}`}
                                value={machine}
                                isOptionEqualToValue={(o, v) => o.equipment_id === v.equipment_id}
                                onChange={(_, v) => setMachine(v)}
                                renderInput={(p) => <TextField {...p} label="Machine (by serial number)" size="small" />}
                            />
                            <Button variant="contained" onClick={start} disabled={busy || !device || !machine}
                                startIcon={<InstallIcon />} sx={{ alignSelf: 'flex-start', fontWeight: 700, textTransform: 'none' }}>
                                Start installation
                            </Button>
                        </Stack>
                    </Card>

                    {/* ── Edit a finished installation ───────────────────────── */}
                    <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.25 }}>Edit an installation</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Move a fitted TCU onto a different machine, or take it off one. Every fleet screen
                            attributes this unit's telemetry to whatever machine is set here, so the change is
                            confirmed and recorded in the history below.
                        </Typography>

                        {editError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setEditError('')}>{editError}</Alert>}
                        {editDone && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setEditDone('')}>{editDone}</Alert>}

                        <Autocomplete
                            options={devices}
                            value={picked}
                            onChange={(_, v) => pick(v)}
                            isOptionEqualToValue={(o, v) => o.device_id === v.device_id}
                            getOptionLabel={(o) => o.device_id || ''}
                            filterOptions={(opts, { inputValue }) => {
                                // Installers know a unit by its machine serial as often as its IMEI.
                                const q = inputValue.trim().toLowerCase();
                                if (!q) return opts;
                                return opts.filter(o =>
                                    String(o.device_id || '').toLowerCase().includes(q) ||
                                    String(o.serial_number || '').toLowerCase().includes(q) ||
                                    String(o.equipment_id || '').toLowerCase().includes(q));
                            }}
                            renderOption={(props, o) => (
                                <Box component="li" {...props} key={o.device_id}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                                            {o.device_id}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {o.equipment_id ? `on ${machineLabel(o)} · ${o.equipment_id}` : 'not installed'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} size="small" label="Installed device"
                                    placeholder="Search by IMEI, serial or machine" />
                            )}
                        />

                        {!picked ? (
                            <Box sx={{ py: 5, textAlign: 'center' }}>
                                <EditIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                    Pick a device to change what it is installed on
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    {installedCount} installed · {devices.length - installedCount} spare
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1.75} sx={{ mt: 2 }}>
                                {/* Where it is now — the thing being changed. */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip size="small" label={`IMEI ${picked.device_id}`}
                                        sx={{ fontWeight: 800, fontFamily: 'monospace', height: 24 }} />
                                    <Chip size="small" variant="outlined"
                                        color={picked.equipment_id ? 'primary' : 'default'}
                                        label={picked.equipment_id
                                            ? `Now on ${machineLabel(picked)}${currentMachine?.model_number ? ` · ${currentMachine.model_number}` : ''}`
                                            : 'Not installed — this will be its first machine'}
                                        sx={{ fontWeight: 700, height: 24 }} />
                                </Box>

                                <Autocomplete
                                    options={[...machines]
                                        .filter(m => m.equipment_id !== picked.equipment_id)
                                        .sort((a, b) => (a.mapped_device ? 1 : 0) - (b.mapped_device ? 1 : 0))}
                                    groupBy={(m) => (m.mapped_device ? 'Already has a device' : 'Free machines')}
                                    getOptionLabel={(m) => `${m.serial_number || m.equipment_id} · ${[m.brand, m.model_number].filter(Boolean).join(' ')}${m.mapped_device ? ` (device: ${m.mapped_device})` : ''}`}
                                    value={moveTo}
                                    isOptionEqualToValue={(o, v) => o.equipment_id === v.equipment_id}
                                    onChange={(_, v) => setMoveTo(v)}
                                    renderInput={(p) => <TextField {...p} size="small" label="Move it onto this machine"
                                        placeholder="Machine serial number" />}
                                />

                                <TextField label="Why (recorded with the change)" value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)} size="small" fullWidth
                                    placeholder="Wrong machine picked at commissioning / unit moved to another machine…" />

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Button variant="contained" disabled={saving || !moveTo}
                                        onClick={() => setAsk({ kind: 'remap' })}
                                        startIcon={<RemapIcon />} sx={{ fontWeight: 700, textTransform: 'none' }}>
                                        Move to this machine
                                    </Button>
                                    <Button color="error" variant="outlined" disabled={saving || !picked.equipment_id}
                                        onClick={() => setAsk({ kind: 'unmap' })}
                                        startIcon={<UnmapIcon />} sx={{ fontWeight: 700, textTransform: 'none' }}>
                                        Take it off the machine
                                    </Button>
                                    {saving && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Taking a unit off leaves the machine with no telemetry until another TCU is
                                    commissioned onto it. The unit's own history stays under its IMEI either way.
                                </Typography>
                            </Stack>
                        )}
                    </Card>
                  </Stack>
                </Grid>

                {/* ── Every fitted TCU ───────────────────────────────────────── */}
                <Grid size={{ xs: 12, lg: 5 }}>
                    <Card variant="outlined" sx={{ borderRadius: '14px', p: 2.5, position: { lg: 'sticky' }, top: 16 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography variant="subtitle1" fontWeight={800}>Installed devices</Typography>
                            <Chip size="small" label={installedCount} sx={{ height: 20, fontWeight: 800 }} />
                            <Button size="small" onClick={load}
                                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                                sx={{ ml: 'auto', textTransform: 'none', fontWeight: 700 }}>
                                Refresh
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            {installedCount} on a machine · {devices.length - installedCount} spare.
                            Click a unit to change what it is installed on.
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                            <TextField
                                value={query} onChange={(e) => setQuery(e.target.value)}
                                size="small" fullWidth placeholder="Search IMEI, serial or machine"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <ToggleButtonGroup exclusive size="small" value={fitment}
                                onChange={(_, v) => v && setFitment(v)}>
                                <ToggleButton value="installed" sx={{ textTransform: 'none', fontWeight: 700, px: 1.25 }}>Installed</ToggleButton>
                                <ToggleButton value="spare" sx={{ textTransform: 'none', fontWeight: 700, px: 1.25 }}>Spare</ToggleButton>
                                <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700, px: 1.25 }}>All</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>

                        {visible.length === 0 ? (
                            <Box sx={{ py: 5, textAlign: 'center' }}>
                                <TcuIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                    {devices.length === 0 ? 'No TCU registered yet' : 'No device matches that search'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Stack spacing={0.75} sx={{ maxHeight: '62vh', overflowY: 'auto', pr: 0.5 }}>
                                    {visible.map(d => (
                                        <InstalledRow key={d.device_id} d={d}
                                            selected={picked?.device_id === d.device_id}
                                            onPick={pick} />
                                    ))}
                                </Stack>
                                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                                    Showing {visible.length} of {devices.length}
                                </Typography>
                            </>
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* The mapping change is confirmed before it is written — and again,
                with the server's own reason, when it would displace another unit. */}
            <Dialog open={!!ask} onClose={() => setAsk(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {ask?.kind === 'unmap' ? 'Take the device off this machine?' : 'Move this device?'}
                </DialogTitle>
                <DialogContent>
                    {ask?.message ? (
                        <Alert severity="warning" sx={{ mb: 1.5 }}>{ask.message}</Alert>
                    ) : null}
                    <Typography variant="body2">
                        {ask?.kind === 'unmap' ? (
                            <>
                                <strong>{picked?.device_id}</strong> comes off <strong>{machineLabel(picked)}</strong>.
                                That machine stops reporting until another TCU is commissioned onto it.
                            </>
                        ) : (
                            <>
                                <strong>{picked?.device_id}</strong> moves
                                {picked?.equipment_id ? <> from <strong>{machineLabel(picked)}</strong></> : null}
                                {' '}onto <strong>{machineLabel(moveTo)}</strong>. From now on its telemetry is
                                attributed to that machine everywhere.
                            </>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAsk(null)}>Cancel</Button>
                    <Button color={ask?.kind === 'unmap' ? 'error' : 'primary'} variant="contained"
                        disabled={saving}
                        onClick={() => applyMapping(ask.kind, !!ask.message)}
                        sx={{ fontWeight: 700, textTransform: 'none' }}>
                        {ask?.message ? 'Yes, move it anyway' : ask?.kind === 'unmap' ? 'Take it off' : 'Move it'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Card variant="outlined" sx={{ borderRadius: '14px', overflow: 'hidden', mt: 2.5 }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={800}>Recent installations</Typography>
                </Box>
                {history.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No installations recorded yet.</Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Hours (disp / TCU)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Fuel (disp / TCU)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map(r => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.device_id}</TableCell>
                                    <TableCell>{r.machine_serial || r.equipment_id}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={r.status}
                                            color={r.status === 'COMMISSIONED' ? 'success'
                                                : r.status === 'FAILED' ? 'error'
                                                    : r.status === 'REMAPPED' ? 'warning'
                                                        : r.status === 'UNMAPPED' ? 'default' : 'info'}
                                            sx={{ fontWeight: 700, height: 20 }} />
                                    </TableCell>
                                    <TableCell sx={{ color: r.hours_match === 0 ? '#dc2626' : 'inherit' }}>
                                        {r.display_engine_hours ?? '—'} / {r.telemetry_engine_hours ?? '—'}
                                    </TableCell>
                                    <TableCell sx={{ color: r.fuel_match === 0 ? '#dc2626' : 'inherit' }}>
                                        {r.display_fuel_pct ?? '—'} / {r.telemetry_fuel_pct ?? '—'}
                                    </TableCell>
                                    <TableCell>{r.installed_by || '—'}</TableCell>
                                    <TableCell>{fmt(r.finished_at || r.created_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </Box>
    );
};

export default InstallationTab;
